import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { History, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { StatusBadge } from "@/components/StatusBadge";
import { EnvironmentBadge } from "@/components/EnvironmentBadge";
import { Pagination } from "@/components/Pagination";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/datetime";

const fmtDuration = formatDuration;

export default function ProjectRunsPage() {
  const { projectId } = useParams();
  const [runs, setRuns] = useState(null);
  const [total, setTotal] = useState(0);
  const [envs, setEnvs] = useState([]);
  const [envFilter, setEnvFilter] = useState("all");
  const [browserFilter, setBrowserFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PER_PAGE = 50;

  useEffect(() => {
    api.get(`/projects/${projectId}/environments`).then((r) => setEnvs(r.data.items || [])).catch(() => {});
  }, [projectId]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [envFilter, browserFilter, statusFilter]);

  useEffect(() => {
    const params = new URLSearchParams({
      page: String(page),
      per_page: String(PER_PAGE),
    });
    if (envFilter !== "all") params.set("environment_id", envFilter);
    if (browserFilter !== "all") params.set("browser", browserFilter);
    if (statusFilter !== "all") params.set("status", statusFilter);
    api.get(`/projects/${projectId}/runs?${params.toString()}`)
      .then((r) => {
        setRuns(r.data.items || []);
        setTotal(r.data.total || 0);
      })
      .catch((e) => toast.error(formatApiError(e)));
  }, [projectId, envFilter, browserFilter, statusFilter, page]);

  const envById = (id) => envs.find((e) => e.id === id);

  const columns = [
    {
      key: "name",
      label: "Run",
      mobileVariant: "title",
      render: (r) => (
        <div className="flex flex-col">
          <Link
            to={r.kind === "suite" ? `/suite-runs/${r.id}` : `/runs/${r.id}`}
            data-testid={`run-link-${r.id}`}
            className="rt-primary hover:text-[#3B82F6] transition-colors inline-flex items-center gap-2"
          >
            <span>{r.name || "Untitled"}</span>
            <span className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{r.kind}</span>
          </Link>
          {r.triggered_by_email && (
            <span className="text-[11px] text-[#94A3B8] mt-0.5" data-testid={`run-triggered-${r.id}`}>
              by {r.triggered_by_email}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "env",
      label: "Environment",
      render: (r) => {
        const env = envById(r.environment_id);
        return (
          <div className="flex flex-col gap-1">
            {env ? <EnvironmentBadge type={env.type} name={env.name} /> : <span className="text-xs text-[#94A3B8]">—</span>}
            <span className="text-[11px] font-mono text-[#94A3B8] capitalize">{r.browser || "chromium"}</span>
          </div>
        );
      },
    },
    {
      key: "started",
      label: "Started",
      render: (r) => (
        <div className="flex flex-col">
          <span className="text-xs text-[#64748B]" data-testid={`run-started-${r.id}`}>
            {formatDateTime(r.started_at)}
          </span>
          <span className="text-[11px] text-[#94A3B8] mt-0.5">
            {fmtDuration(r.duration_ms)}
          </span>
        </div>
      ),
    },
    {
      key: "result",
      label: "Result",
      align: "right",
      mobileVariant: "actions",
      render: (r) => (
        <div className="flex items-center justify-end gap-2">
          <div className="flex flex-col items-end gap-0.5">
            <StatusBadge status={r.status} />
            {r.kind === "suite" && (
              <span className="text-[11px] text-[#94A3B8]">
                {r.passed_count}/{r.total_count} passed
              </span>
            )}
          </div>
          <Link
            to={r.kind === "suite" ? `/suite-runs/${r.id}` : `/runs/${r.id}`}
            data-testid={`view-report-${r.id}`}
            aria-label="View report"
            title="View report"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] bg-white text-[#64748B] hover:bg-[#F8FAFC] hover:border-[#3B82F6] hover:text-[#3B82F6] transition-colors"
          >
            <ArrowRight size={14} />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="project-runs-page">
      <PageHeader
        title="Runs"
        description="Historical execution timeline across all tests and suites in this project."
      />

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[#64748B]">Environment</label>
          <Select value={envFilter} onValueChange={setEnvFilter}>
            <SelectTrigger className="h-9 w-44 rounded-md border-[#E5E7EB] text-sm" data-testid="runs-env-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All environments</SelectItem>
              {envs.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[#64748B]">Browser</label>
          <Select value={browserFilter} onValueChange={setBrowserFilter}>
            <SelectTrigger className="h-9 w-36 rounded-md border-[#E5E7EB] text-sm" data-testid="runs-browser-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="chromium">Chromium</SelectItem>
              <SelectItem value="firefox">Firefox</SelectItem>
              <SelectItem value="webkit">WebKit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[#64748B]">Result</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36 rounded-md border-[#E5E7EB] text-sm" data-testid="runs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="passed">Passed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="running">Running</SelectItem>
              <SelectItem value="skipped">Skipped</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {runs === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B] flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : runs.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-runs-message">
          <History size={24} className="mx-auto mb-3 text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg text-[#0F172A]">No runs yet</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Run a test or a suite to see results here.
          </p>
        </div>
      ) : (
        <>
          <ResponsiveTable
            testId="runs-table"
            columns={columns}
            rows={runs}
            getRowTestId={(r) => `run-row-${r.id}`}
          />
          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={total}
            onChange={setPage}
            testId="runs-pagination"
          />
        </>
      )}
    </div>
  );
}
